// src/pages/dataInput/utils/indexSheetParser.ts
// ─────────────────────────────────────────────────────────────────────────────
// Detects and parses an "Index" sheet in an Excel workbook.
//
// Recognised column headers (case + whitespace insensitive):
//   Entity Name | Link to Sheet | Full Entity Name | Source | Entity Description
//
// Returns Map<sheetTabName, IndexEntry> so FileSource can auto-fill
// SheetConfig.description and SheetConfig.displayName.
// ─────────────────────────────────────────────────────────────────────────────

import * as XLSX from "xlsx";

export interface IndexEntry {
  sheetName:   string;   // raw value from "Entity Name" / "Link to Sheet" column
  fullName:    string;   // "Full Entity Name"
  source:      string;   // "Source"
  description: string;   // "Entity Description"
}

// ── Column alias sets ─────────────────────────────────────────────────────────
const ENTITY_NAME_ALIASES = new Set([
  "entity name", "entityname", "entity_name",
  "link to sheet", "linktosheet", "link_to_sheet",
  "sheet name", "sheetname", "sheet_name",
]);
const FULL_NAME_ALIASES = new Set([
  "full entity name", "fullentityname", "full_entity_name",
  "full name", "fullname", "long name",
]);
const SOURCE_ALIASES = new Set([
  "source", "data source", "datasource", "src",
]);
const DESCRIPTION_ALIASES = new Set([
  "entity description", "entitydescription", "entity_description",
  "description", "desc", "business description",
]);

const norm = (s: any) => String(s ?? "").trim().toLowerCase().replace(/[\s_-]+/g, " ");

function findCol(headers: string[], aliases: Set<string>): number {
  return headers.findIndex(h => aliases.has(norm(h)));
}

// ── Index sheet name detection ────────────────────────────────────────────────
const INDEX_SHEET_NAMES = new Set([
  "index", "contents", "legend", "overview", "toc", "readme", "table of contents",
]);

export function looksLikeIndexSheet(sheetName: string): boolean {
  const n = norm(sheetName);
  return INDEX_SHEET_NAMES.has(n) || n.startsWith("index") || n.startsWith("contents");
}

// ── Parse a single sheet as an Index sheet ───────────────────────────────────
export function parseIndexSheet(ws: XLSX.WorkSheet): Map<string, IndexEntry> | null {
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1, defval: "", blankrows: false, raw: false,
  }) as any[][];

  if (aoa.length < 2) return null;

  // Scan first 5 rows for header row — must have at least entity-name + description
  let hdrRow  = -1;
  let headers: string[] = [];

  for (let r = 0; r < Math.min(5, aoa.length); r++) {
    const row = aoa[r].map((c: any) => String(c ?? "").trim());
    if (findCol(row, ENTITY_NAME_ALIASES) >= 0 && findCol(row, DESCRIPTION_ALIASES) >= 0) {
      hdrRow  = r;
      headers = row;
      break;
    }
  }

  if (hdrRow < 0) return null;

  const nameIdx = findCol(headers, ENTITY_NAME_ALIASES);
  const fullIdx = findCol(headers, FULL_NAME_ALIASES);
  const srcIdx  = findCol(headers, SOURCE_ALIASES);
  const descIdx = findCol(headers, DESCRIPTION_ALIASES);

  if (nameIdx < 0 || descIdx < 0) return null;

  const result = new Map<string, IndexEntry>();

  for (let r = hdrRow + 1; r < aoa.length; r++) {
    const row     = aoa[r];
    const rawName = String(row[nameIdx] ?? "").trim();
    if (!rawName) continue;

    result.set(rawName, {
      sheetName:   rawName,
      fullName:    fullIdx >= 0 ? String(row[fullIdx] ?? "").trim() : rawName,
      source:      srcIdx  >= 0 ? String(row[srcIdx]  ?? "").trim() : "",
      description: descIdx >= 0 ? String(row[descIdx] ?? "").trim() : "",
    });
  }

  return result.size > 0 ? result : null;
}

// ── Main entry: scan entire workbook ─────────────────────────────────────────
/**
 * Returns a Map<sheetTabName, IndexEntry> built from the Index sheet,
 * or null if no Index sheet is found / parsed successfully.
 *
 * The map is keyed by the value in the "Entity Name / Link to Sheet" column
 * which should match the actual Excel tab name.
 */
export function extractIndexEntries(wb: XLSX.WorkBook): Map<string, IndexEntry> | null {
  // First pass: sheets whose tab name looks like an index
  for (const name of wb.SheetNames) {
    if (looksLikeIndexSheet(name)) {
      const entries = parseIndexSheet(wb.Sheets[name]);
      if (entries) return entries;
    }
  }
  // Second pass: any sheet that structurally looks like an index (unusual tab name)
  for (const name of wb.SheetNames) {
    if (looksLikeIndexSheet(name)) continue;
    const entries = parseIndexSheet(wb.Sheets[name]);
    if (entries) return entries;
  }
  return null;
}